import React, { useEffect } from 'react'
import { useFragment, graphql } from 'react-relay'

import { Separator, YStack, XStack, StackProps } from 'tamagui'

import { useLocation } from 'react-router-dom'

import { HOST_NAME, IMAGEKIT_URL } from '../data/config/url'

import { Card } from './Card'
import { H3, Paragraph } from './Text'
import { ExternalLink } from './Link'
import { Image } from './Image'

import { SponsorsSection_query$key } from './__generated__/SponsorsSection_query.graphql'
import { SponsorsSectionSponsor_guildSponsorship$key } from './__generated__/SponsorsSectionSponsor_guildSponsorship.graphql'

export const SponsorsSection = ({
  query,
  ...props
}: {
  query: SponsorsSection_query$key
} & StackProps) => {
  const data = useFragment(
    graphql`
      fragment SponsorsSection_query on Query {
        sponsorships(first: 2) {
          edges {
            node {
              ...SponsorsSectionSponsor_guildSponsorship
            }

            cursor
          }
        }
      }
    `,
    query
  )

  if (!data.sponsorships?.edges.length) {
    return null
  }

  return (
    <Card {...props} gap='$4' data-nosnippet>
      <Paragraph size='$4' fontWeight='$5' color='$grey7'>
        {data.sponsorships.edges.length === 1
          ? 'Platform Sponsor'
          : 'Platform Sponsors'}
      </Paragraph>

      <YStack gap='$4'>
        {data.sponsorships.edges.map((edge, index) => {
          if (!edge.node) {
            return null
          }

          return (
            <React.Fragment key={edge.cursor}>
              {index !== 0 && <Separator borderColor='$grey5' />}

              <Sponsor sponsor={edge.node} />
            </React.Fragment>
          )
        })}
      </YStack>
    </Card>
  )
}

const Sponsor = ({
  sponsor,
}: {
  sponsor: SponsorsSectionSponsor_guildSponsorship$key
}) => {
  const data = useFragment(
    graphql`
      fragment SponsorsSectionSponsor_guildSponsorship on GuildSponsorship {
        rowId
        description
        webUrl
        linkText

        lightModeImage {
          rowId
          contentType

          width
          height
        }

        guild {
          name
        }
      }
    `,
    sponsor
  )

  const location = useLocation()

  useEffect(() => {
    externalTrackView(
      data.rowId,
      location.state?.backgroundLocation?.pathname || location.pathname
    )
  }, [
    data.rowId,
    location.state?.backgroundLocation?.pathname || location.pathname,
  ])

  const TRACKING_URL = `${HOST_NAME}/tw/${data.rowId}`

  return (
    <YStack
      tag='a'
      gap='$4'
      cursor='pointer'
      href={TRACKING_URL}
      target='_blank'
      style={{
        textDecorationLine: 'none',
      }}
    >
      {data.lightModeImage ? (
        <Image
          alt={`Sponsor Logo for ${data.guild?.name}`}
          src={`${IMAGEKIT_URL}/tr:w-150,dpr-3/${
            data.lightModeImage?.rowId
          }.${data.lightModeImage?.contentType.toLowerCase()}`}
          maxWidth={150}
          // width='100%'
          // height='100%'
          // height={87 / 2}
          // resizeMode='contain'
        />
      ) : (
        <XStack gap='$4'>
          <H3 size='$6'>{data.guild?.name}</H3>
        </XStack>
      )}

      <Paragraph size='$4'>{data.description}</Paragraph>

      <Paragraph
        size='$4'
        color='$darkBlue9'
        cursor='pointer'
        hoverStyle={{ textDecorationLine: 'underline' }}
      >
        {data.linkText}
      </Paragraph>
    </YStack>
  )
}

const TRACK_VIEW_TIMERS: Record<string, NodeJS.Timeout> = {}

const externalTrackView = async (
  guildSponsorshipId: string,
  pathname: string
) => {
  const key = [guildSponsorshipId, pathname].join('-')

  const existingTimerID = TRACK_VIEW_TIMERS[key]

  if (existingTimerID) {
    clearTimeout(existingTimerID)
  }

  TRACK_VIEW_TIMERS[key] = setTimeout(async () => {
    try {
      await fetch(`${HOST_NAME}/tv/${guildSponsorshipId}`, { method: 'POST' })
    } catch (err) {}

    delete TRACK_VIEW_TIMERS[key]
  }, 100)
}
